import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useNotifications } from "../../../context/NotificationContext";
import { storeRequestApi } from "../../../api/storeRequestApi";
import { websiteApi } from "../../../api/websiteApi";
import { onboardingApi } from "../../../api/onboardingApi";
import { formatPersonName } from "../../../utils/formatters";
import {
  Store,
  Globe,
  Clock,
  CheckCircle2,
  ListTodo,
  ClipboardCheck,
  ShoppingCart,
  AlertTriangle,
  XCircle,
  MessageSquare,
  User,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  Plus,
  Check,
  LayoutDashboard,
} from "lucide-react";
import "./ClientDashboard.css";

const STAGES = [
  { key: "pending", label: "Submitted" },
  { key: "approved", label: "Approved" },
  { key: "in_progress", label: "Setup" },
  { key: "ready_for_review", label: "Review" },
  { key: "live", label: "Live" },
];

const TABS = [
  { key: "overview", label: "Overview", Icon: LayoutDashboard },
  { key: "store", label: "Online store", Icon: Store },
  { key: "website", label: "Website", Icon: Globe },
];

const formatDate = (value) => {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Date unavailable"
    : date.toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

const isValidRequestId = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value || ""),
  );

const toTime = (value) => {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const labelOf = (status) => String(status || "pending").replace(/_/g, " ");

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

/* ------------------------- Status copy per product ------------------------ */

const getStoreDisplay = (store, progress) => {
  if (!store) {
    return {
      title: "You don't have a store yet",
      description:
        "Tell us about your business and we'll set up your online store.",
      action: "Request a store",
      actionUrl: "/request-store",
      Icon: Store,
      accent: "brand",
    };
  }

  const detailsUrl = isValidRequestId(store.StoreRequestID)
    ? `/request-status/${store.StoreRequestID}`
    : "/dashboard";
  const statusMap = {
    pending: {
      title: "Request under review",
      description: `We received request #${store.RequestNumber} and our team is reviewing it.`,
      action: "View request",
      actionUrl: detailsUrl,
      Icon: Clock,
      accent: "amber",
    },
    approved: {
      title: "Your store is approved",
      description:
        "Your request was approved. Setup will begin shortly. Follow updates here.",
      action: "View setup progress",
      actionUrl: detailsUrl,
      Icon: CheckCircle2,
      accent: "emerald",
    },
    in_progress: {
      title: "Setup in progress",
      description: `Your store setup is ${progress}% complete.`,
      action: "View setup progress",
      actionUrl: detailsUrl,
      Icon: ListTodo,
      accent: "blue",
    },
    ready_for_review: {
      title: "Final review",
      description:
        "Setup is complete. Our team is doing a final check before launch.",
      action: "View request",
      actionUrl: detailsUrl,
      Icon: ClipboardCheck,
      accent: "purple",
    },
    live: {
      title: "Your store is live",
      description: "Your store is published and open to visitors.",
      action: store.StoreUrl ? "Open store" : "View request",
      actionUrl: store.StoreUrl || detailsUrl,
      external: Boolean(store.StoreUrl),
      Icon: ShoppingCart,
      accent: "emerald",
    },
    suspended: {
      title: "Store suspended",
      description:
        "Your store is currently suspended. Contact support to get it back online.",
      action: "Contact support",
      actionUrl: "/support",
      Icon: AlertTriangle,
      accent: "rose",
    },
    rejected: {
      title: "Request declined",
      description:
        "We couldn't approve this request. Review the feedback or contact support.",
      action: "Contact support",
      actionUrl: "/support",
      Icon: XCircle,
      accent: "rose",
    },
  };

  return statusMap[store.Status] || statusMap.pending;
};

const getWebsiteDisplay = (site) => {
  if (!site) {
    return {
      title: "No website project yet",
      description:
        "Start a website project and follow every update right here.",
      action: "Start a website project",
      actionUrl: "/website-onboarding",
      Icon: Globe,
      accent: "brand",
    };
  }

  const detailsUrl = `/website-request-status/${site.WebsiteRequestID}`;
  const statusMap = {
    pending: {
      title: "Request under review",
      description:
        "We received your website request and our team is reviewing it.",
      action: "View request",
      actionUrl: detailsUrl,
      Icon: Clock,
      accent: "amber",
    },
    approved: {
      title: "Your website is approved",
      description: "Your request was approved. Work will begin shortly.",
      action: "View request",
      actionUrl: detailsUrl,
      Icon: CheckCircle2,
      accent: "emerald",
    },
    in_progress: {
      title: "Website in progress",
      description: "Our team is working on your website.",
      action: "View progress",
      actionUrl: detailsUrl,
      Icon: ListTodo,
      accent: "blue",
    },
    ready_for_review: {
      title: "Final review",
      description:
        "Your website is built. Our team is doing a final check before launch.",
      action: "View request",
      actionUrl: detailsUrl,
      Icon: ClipboardCheck,
      accent: "purple",
    },
    live: {
      title: "Your website is live",
      description: "Your website is published and open to visitors.",
      action: "View request",
      actionUrl: detailsUrl,
      Icon: Globe,
      accent: "emerald",
    },
    rejected: {
      title: "Request declined",
      description:
        "We couldn't approve this request. Review the feedback or contact support.",
      action: "Contact support",
      actionUrl: "/support",
      Icon: XCircle,
      accent: "rose",
    },
  };

  const key = String(site.Status || "pending").toLowerCase();
  return (
    statusMap[key] || {
      title: `Request ${labelOf(key)}`,
      description: "Open your request for the latest details.",
      action: "View request",
      actionUrl: detailsUrl,
      Icon: Globe,
      accent: "brand",
    }
  );
};

/* ------------------------------- Small parts ------------------------------ */

const StatusPill = ({ status }) => (
  <span className={`status-pill status-${status}`}>
    <span className="dot" />
    {labelOf(status)}
  </span>
);

const ActionButton = ({ display, onRun, variant = "primary" }) => (
  <button
    type="button"
    className={`btn btn-${variant}`}
    onClick={() => onRun(display)}
  >
    {display.action}
    {display.external ? (
      <ExternalLink size={16} aria-hidden="true" />
    ) : (
      <ArrowRight size={16} aria-hidden="true" />
    )}
  </button>
);

const ProgressBar = ({ value }) => (
  <div className="progress-container">
    <div className="progress-header">
      <span>Setup progress</span>
      <strong>{value}%</strong>
    </div>
    <div
      className="progress-bar-track"
      role="progressbar"
      aria-label="Setup progress"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="progress-bar-fill" style={{ width: `${value}%` }} />
    </div>
  </div>
);

const StageTracker = ({ status, label }) => {
  const stageIndex = STAGES.findIndex((s) => s.key === status);
  if (stageIndex === -1) return null;
  return (
    <ol className="stage-tracker" aria-label={label}>
      {STAGES.map((stage, i) => {
        const state =
          i < stageIndex ? "done" : i === stageIndex ? "current" : "todo";
        return (
          <li
            key={stage.key}
            className={`stage stage-${state}`}
            aria-current={state === "current" ? "step" : undefined}
          >
            <span className="stage-marker" aria-hidden="true">
              {state === "done" ? <Check size={14} strokeWidth={3} /> : null}
            </span>
            <span className="stage-label">
              {stage.label}
              <span className="sr-only">
                {state === "done"
                  ? " (completed)"
                  : state === "current"
                    ? " (current stage)"
                    : ""}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const InlineError = ({ message, onRetry }) => (
  <div className="dashboard-load-error" role="alert">
    <AlertTriangle size={18} aria-hidden="true" />
    <span>{message}</span>
    <button type="button" className="link-btn" onClick={onRetry}>
      Try again
    </button>
  </div>
);

/* Overview: one compact card per product. */
const ProductCard = ({
  eyebrow,
  display,
  status,
  progress,
  loading,
  error,
  onRetry,
  onRun,
  onDetails,
}) => {
  const Icon = display.Icon;
  const stageIndex = STAGES.findIndex((s) => s.key === status);
  return (
    <section className={`dashboard-card product-card accent-${display.accent}`}>
      <div className="product-card-top">
        <span className="eyebrow">{eyebrow}</span>
        {status && <StatusPill status={status} />}
      </div>

      {loading ? (
        <div className="product-card-loading" role="status">
          <span className="skeleton sk-line" />
          <span className="skeleton sk-line short" />
          <span className="sr-only">Loading</span>
        </div>
      ) : error ? (
        <InlineError message={error} onRetry={onRetry} />
      ) : (
        <>
          <div className="product-card-body">
            <div className="status-icon-wrapper">
              <Icon size={22} aria-hidden="true" />
            </div>
            <div>
              <h2 className="product-card-title">{display.title}</h2>
              <p className="status-description">{display.description}</p>
              {stageIndex !== -1 && (
                <p className="stage-hint">
                  Stage {stageIndex + 1} of {STAGES.length} ·{" "}
                  {STAGES[stageIndex].label}
                </p>
              )}
            </div>
          </div>
          {status === "in_progress" && typeof progress === "number" && (
            <ProgressBar value={progress} />
          )}
          <div className="product-card-actions">
            <ActionButton display={display} onRun={onRun} />
            <button type="button" className="link-action" onClick={onDetails}>
              View details
              <ChevronRight size={15} aria-hidden="true" />
            </button>
          </div>
        </>
      )}
    </section>
  );
};

/* Product tab: the full status card with tracker. */
const StatusCard = ({
  headingId,
  display,
  status,
  progress,
  trackerLabel,
  onRun,
  full,
}) => {
  const Icon = display.Icon;
  return (
    <section
      className={`dashboard-card status-card accent-${display.accent} ${full ? "is-full" : ""}`}
      aria-labelledby={headingId}
    >
      <div className="status-top-row">
        <div className="status-title-wrap">
          <div className="status-icon-wrapper">
            <Icon size={22} aria-hidden="true" />
          </div>
          <div>
            <h2 id={headingId}>{display.title}</h2>
            <p className="status-description">{display.description}</p>
          </div>
        </div>
        {status && <StatusPill status={status} />}
      </div>

      <StageTracker status={status} label={trackerLabel} />

      {status === "in_progress" && typeof progress === "number" && (
        <ProgressBar value={progress} />
      )}

      <div className="status-actions">
        <ActionButton display={display} onRun={onRun} />
      </div>
    </section>
  );
};

const DetailsCard = ({ headingId, title, rows }) => (
  <section className="dashboard-card details-card" aria-labelledby={headingId}>
    <h2 id={headingId} className="card-title">
      {title}
    </h2>
    <dl className="details-list">
      {rows.map((row) => (
        <div className="detail-row" key={row.label}>
          <dt>{row.label}</dt>
          <dd className={row.mono ? "code-font" : undefined}>
            {row.href ? (
              <a
                href={row.href}
                target="_blank"
                rel="noopener noreferrer"
                className="store-link"
              >
                {row.value}
              </a>
            ) : (
              row.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  </section>
);

const RequestList = ({
  items,
  onOpen,
  showProduct,
  emptyIcon: EmptyIcon,
  emptyText,
  emptyLabel,
  onEmpty,
}) =>
  items.length > 0 ? (
    <ul className="recent-requests-list">
      {items.map((item) => {
        const Icon = item.product === "website" ? Globe : Store;
        return (
          <li className="recent-request-row" key={item.key}>
            <button
              type="button"
              className="recent-request-button"
              onClick={() => onOpen(item.href)}
            >
              <span className={`recent-request-icon product-${item.product}`}>
                <Icon size={18} aria-hidden="true" />
              </span>
              <span className="recent-request-info">
                <strong>{item.title}</strong>
                <span className="recent-request-sub">
                  {showProduct && (
                    <span className="product-chip">
                      {item.product === "website" ? "Website" : "Store"}
                    </span>
                  )}
                  {item.meta.map((m) => (
                    <span
                      key={m.text}
                      className={m.mono ? "code-font" : undefined}
                    >
                      {m.text}
                    </span>
                  ))}
                </span>
              </span>
              <span className="recent-request-meta">
                <StatusPill status={item.status} />
                {item.payment && (
                  <span className={`payment-status payment-${item.payment}`}>
                    Payment {item.payment}
                  </span>
                )}
              </span>
              <ChevronRight
                size={16}
                className="row-arrow"
                aria-hidden="true"
              />
            </button>
          </li>
        );
      })}
    </ul>
  ) : (
    <div className="recent-requests-empty">
      <EmptyIcon size={24} aria-hidden="true" />
      <p>{emptyText}</p>
      {onEmpty && (
        <button type="button" className="btn btn-secondary" onClick={onEmpty}>
          {emptyLabel}
        </button>
      )}
    </div>
  );

const NewRequestMenu = ({ onStore, onWebsite }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (action) => {
    setOpen(false);
    action();
  };

  return (
    <div className="new-request" ref={ref}>
      <button
        type="button"
        className="btn btn-primary"
        aria-expanded={open}
        aria-controls="new-request-menu"
        onClick={() => setOpen((current) => !current)}
      >
        <Plus size={16} aria-hidden="true" />
        New request
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <ul className="new-request-menu" id="new-request-menu">
          <li>
            <button
              type="button"
              className="action-tile"
              onClick={() => choose(onStore)}
            >
              <span className="tile-icon">
                <Store size={18} aria-hidden="true" />
              </span>
              <span className="tile-info">
                <span className="tile-title">Online store</span>
                <span className="tile-desc">Set up a new store</span>
              </span>
            </button>
          </li>
          <li>
            <button
              type="button"
              className="action-tile"
              onClick={() => choose(onWebsite)}
            >
              <span className="tile-icon">
                <Globe size={18} aria-hidden="true" />
              </span>
              <span className="tile-info">
                <span className="tile-title">Website</span>
                <span className="tile-desc">Start a website project</span>
              </span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

/* --------------------------------- Page ---------------------------------- */

const ClientDashboard = () => {
  const [storeStatus, setStoreStatus] = useState(null);
  const [storeRequests, setStoreRequests] = useState([]);
  const [websiteRequests, setWebsiteRequests] = useState([]);
  const [websiteRequestsLoading, setWebsiteRequestsLoading] = useState(true);
  const [websiteRequestsError, setWebsiteRequestsError] = useState("");
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");
  const [progress, setProgress] = useState(0);
  const { user } = useAuth();
  const { socket } = useNotifications();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabRefs = useRef({});

  const requestedTab = searchParams.get("tab");
  const activeTab = TABS.some((tab) => tab.key === requestedTab)
    ? requestedTab
    : "overview";

  const selectTab = (key) => {
    setSearchParams(key === "overview" ? {} : { tab: key }, { replace: true });
  };

  const onTabKeyDown = (event, index) => {
    let next = null;
    if (event.key === "ArrowRight") next = (index + 1) % TABS.length;
    else if (event.key === "ArrowLeft")
      next = (index - 1 + TABS.length) % TABS.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = TABS.length - 1;
    if (next === null) return;
    event.preventDefault();
    const key = TABS[next].key;
    selectTab(key);
    tabRefs.current[key]?.focus();
  };

  const fetchWebsiteRequests = useCallback(async () => {
    try {
      const response = await websiteApi.getClientRequests();
      const requests = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setWebsiteRequests(requests);
      setWebsiteRequestsError("");
    } catch (error) {
      console.error("Error fetching website requests:", error);
      setWebsiteRequestsError(
        error.response?.data?.error ||
          "We couldn't load your website requests. Check your connection and try again.",
      );
    } finally {
      setWebsiteRequestsLoading(false);
    }
  }, []);

  // `silent` refreshes (live socket updates, "Try again") keep the current
  // content on screen instead of swapping the page for a skeleton.
  const fetchDashboardData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await storeRequestApi.getClientRequests();
      const requests = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setStoreRequests(requests);
      setDashboardError("");

      if (requests.length > 0) {
        const latest = requests[0];
        setStoreStatus(latest);

        if (
          latest.Status === "in_progress" &&
          isValidRequestId(latest.StoreRequestID)
        ) {
          try {
            const progressResponse = await onboardingApi.getProgress(
              latest.StoreRequestID,
            );
            const progData = progressResponse.data;
            if (progData) {
              const percentage =
                progData.TotalSteps > 0
                  ? Math.round(
                      (progData.CompletedSteps / progData.TotalSteps) * 100,
                    )
                  : 0;
              setProgress(percentage);
            }
          } catch (progressError) {
            console.error("Error fetching onboarding progress:", progressError);
          }
        } else {
          setProgress(0);
        }
      } else {
        setStoreStatus(null);
        setProgress(0);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setDashboardError(
        error.response?.data?.error ||
          "We couldn't load your store requests. Check your connection and try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    fetchWebsiteRequests();
  }, [fetchWebsiteRequests]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleLiveUpdate = () => fetchDashboardData({ silent: true });
    socket.on("request:updated", handleLiveUpdate);
    socket.on("platform:updated", handleLiveUpdate);
    return () => {
      socket.off("request:updated", handleLiveUpdate);
      socket.off("platform:updated", handleLiveUpdate);
    };
  }, [fetchDashboardData, socket]);

  useEffect(() => {
    if (!socket) return undefined;
    socket.on("website-request:updated", fetchWebsiteRequests);
    return () => socket.off("website-request:updated", fetchWebsiteRequests);
  }, [fetchWebsiteRequests, socket]);

  if (loading) {
    return (
      <div className="client-dashboard" aria-busy="true" aria-live="polite">
        <div className="skeleton sk-title" />
        <div className="skeleton sk-sub" />
        <div className="skeleton sk-tabs" />
        <div className="dashboard-grid">
          <div className="skeleton sk-card sk-half" />
          <div className="skeleton sk-card sk-half" />
        </div>
        <span className="sr-only">Loading your dashboard</span>
      </div>
    );
  }

  /* ----------------------------- Derived data ----------------------------- */

  const latestWebsite = websiteRequests[0] || null;
  const storeState = storeStatus?.Status;
  const websiteState = latestWebsite
    ? String(latestWebsite.Status || "pending").toLowerCase()
    : undefined;
  const storeDisplay = getStoreDisplay(storeStatus, progress);
  const websiteDisplay = getWebsiteDisplay(latestWebsite);
  const canRequestStore =
    !storeStatus || storeState === "pending" || storeState === "rejected";
  const showStoreDetails =
    storeStatus && storeState !== "pending" && storeState !== "rejected";

  const runAction = (display) => {
    if (display.external) {
      window.open(display.actionUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(display.actionUrl);
    }
  };

  const storeItems = storeRequests.filter((request) =>
    isValidRequestId(request.StoreRequestID),
  ).map((request) => ({
    key: `store-${request.StoreRequestID}`,
    product: "store",
    title: request.BusinessName || "Store request",
    meta: [
      {
        text: request.RequestNumber
          ? `#${request.RequestNumber}`
          : "No request number",
        mono: true,
      },
      { text: request.PackageName || "No package" },
      { text: formatDate(request.SubmittedDate) },
    ],
    time: toTime(request.SubmittedDate),
    status: String(request.Status || "pending").toLowerCase(),
    payment: request.PaymentStatus
      ? String(request.PaymentStatus).toLowerCase()
      : "",
    href: `/request-status/${request.StoreRequestID}`,
  }));

  const websiteItems = websiteRequests.map((request) => ({
    key: `website-${request.WebsiteRequestID}`,
    product: "website",
    title: request.BusinessName || "Website request",
    meta: [
      { text: request.PackageName || "No package" },
      { text: formatDate(request.CreatedAt) },
    ],
    time: toTime(request.CreatedAt),
    status: String(request.Status || "pending").toLowerCase(),
    payment: request.PaymentStatus
      ? String(request.PaymentStatus).toLowerCase()
      : "",
    href: `/website-request-status/${request.WebsiteRequestID}`,
  }));

  const activityItems = [...storeItems, ...websiteItems]
    .sort((a, b) => b.time - a.time)
    .slice(0, 6);

  const storeDetails = storeStatus
    ? [
        {
          label: "Business",
          value: storeStatus.BusinessName || "Not provided",
        },
        { label: "Package", value: storeStatus.PackageName || "Standard plan" },
        {
          label: "Request number",
          value: storeStatus.RequestNumber,
          mono: true,
        },
        { label: "Submitted", value: formatDate(storeStatus.SubmittedDate) },
        ...(storeStatus.StoreUrl
          ? [
              {
                label: "Store URL",
                value: storeStatus.StoreUrl,
                href: storeStatus.StoreUrl,
              },
            ]
          : []),
      ]
    : [];

  const websiteDetails = latestWebsite
    ? [
        {
          label: "Business",
          value: latestWebsite.BusinessName || "Not provided",
        },
        { label: "Package", value: latestWebsite.PackageName || "No package" },
        { label: "Submitted", value: formatDate(latestWebsite.CreatedAt) },
        ...(latestWebsite.PaymentStatus
          ? [
              {
                label: "Payment",
                value: labelOf(String(latestWebsite.PaymentStatus)),
              },
            ]
          : []),
      ]
    : [];

  const quickActions = [
    canRequestStore && {
      title: "Request a store",
      desc: "Start a new store setup",
      Icon: Store,
      onClick: () => navigate("/request-store"),
    },
    {
      title: "Start a website project",
      desc: "Tell us what you need built",
      Icon: Globe,
      onClick: () => navigate("/website-onboarding"),
    },
    (storeState === "approved" || storeState === "in_progress") &&
      isValidRequestId(storeStatus?.StoreRequestID) && {
      title: "Store setup progress",
      desc: "Progress, updates and messages",
      Icon: ListTodo,
      onClick: () => navigate(`/request-status/${storeStatus.StoreRequestID}`),
    },
    storeState === "live" &&
      storeStatus.StoreUrl && {
        title: "Open store",
        desc: "Opens in a new tab",
        Icon: ShoppingCart,
        onClick: () =>
          window.open(storeStatus.StoreUrl, "_blank", "noopener,noreferrer"),
      },
    {
      title: "Messages & notifications",
      desc: "Updates from Toora Flex",
      Icon: MessageSquare,
      onClick: () => navigate("/communication"),
    },
    {
      title: "Profile settings",
      desc: "Manage your account details",
      Icon: User,
      onClick: () => navigate("/profile"),
    },
  ].filter(Boolean);

  const tabCount = (key) => {
    if (key === "store") return storeRequests.length;
    if (key === "website")
      return websiteRequestsLoading ? 0 : websiteRequests.length;
    return 0;
  };

  /* ---------------------------------- UI ---------------------------------- */

  return (
    <div className="client-dashboard">
      <header className="dashboard-header">
        <div>
          <h1>
            {getGreeting()}
            {user?.FullName
              ? `, ${formatPersonName(user.FullName, "Client")}`
              : ""}
          </h1>
          <p className="header-subtitle">
            Track your store and website projects, and manage your account.
          </p>
        </div>
        <NewRequestMenu
          onStore={() => navigate("/request-store")}
          onWebsite={() => navigate("/website-onboarding")}
        />
      </header>

      {dashboardError && (
        <InlineError
          message={dashboardError}
          onRetry={() => fetchDashboardData()}
        />
      )}

      <div
        className="dashboard-tabs"
        role="tablist"
        aria-label="Dashboard sections"
      >
        {TABS.map((tab, index) => {
          const selected = tab.key === activeTab;
          const count = tabCount(tab.key);
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              id={`tab-${tab.key}`}
              aria-selected={selected}
              aria-controls={`panel-${tab.key}`}
              tabIndex={selected ? 0 : -1}
              className={`dashboard-tab ${selected ? "is-active" : ""}`}
              ref={(node) => {
                tabRefs.current[tab.key] = node;
              }}
              onClick={() => selectTab(tab.key)}
              onKeyDown={(event) => onTabKeyDown(event, index)}
            >
              <tab.Icon size={16} aria-hidden="true" />
              {tab.label}
              {count > 0 && <span className="tab-count">{count}</span>}
            </button>
          );
        })}
      </div>

      <div
        className="dashboard-panel"
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        tabIndex={0}
        key={activeTab}
      >
        {activeTab === "overview" && (
          <div className="dashboard-grid">
            <ProductCard
              eyebrow="Online store"
              display={storeDisplay}
              status={storeState}
              progress={progress}
              onRun={runAction}
              onDetails={() => selectTab("store")}
            />
            <ProductCard
              eyebrow="Website"
              display={websiteDisplay}
              status={websiteState}
              loading={websiteRequestsLoading}
              error={websiteRequestsError}
              onRetry={fetchWebsiteRequests}
              onRun={runAction}
              onDetails={() => selectTab("website")}
            />

            <section
              className="dashboard-card activity-card"
              aria-labelledby="activity-heading"
            >
              <div className="card-header">
                <h2 id="activity-heading" className="card-title">
                  Recent activity
                </h2>
                <p className="card-subtitle">
                  Your latest requests across the store and website
                </p>
              </div>
              {websiteRequestsLoading && activityItems.length === 0 ? (
                <p role="status" className="card-subtitle">
                  Loading your requests...
                </p>
              ) : (
                <RequestList
                  items={activityItems}
                  onOpen={navigate}
                  showProduct
                  emptyIcon={LayoutDashboard}
                  emptyText="No requests yet. Request a store or start a website project to get going."
                />
              )}
            </section>

            <section
              className="dashboard-card quick-actions-card"
              aria-labelledby="actions-heading"
            >
              <h2 id="actions-heading" className="card-title">
                Quick actions
              </h2>
              <ul className="quick-actions-list">
                {quickActions.map(({ title, desc, Icon, onClick }) => (
                  <li key={title}>
                    <button
                      type="button"
                      onClick={onClick}
                      className="action-tile"
                    >
                      <span className="tile-icon">
                        <Icon size={18} aria-hidden="true" />
                      </span>
                      <span className="tile-info">
                        <span className="tile-title">{title}</span>
                        <span className="tile-desc">{desc}</span>
                      </span>
                      <ChevronRight
                        className="tile-arrow"
                        size={16}
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}

        {activeTab === "store" && (
          <div className="dashboard-grid">
            <StatusCard
              headingId="store-status-heading"
              display={storeDisplay}
              status={storeState}
              progress={progress}
              trackerLabel="Store request stages"
              onRun={runAction}
              full={!showStoreDetails}
            />
            {showStoreDetails && (
              <DetailsCard
                headingId="store-details-heading"
                title="Store details"
                rows={storeDetails}
              />
            )}

            <section
              className="dashboard-card list-card"
              aria-labelledby="store-requests-heading"
            >
              <div className="card-header dashboard-card-header-row">
                <div>
                  <h2 id="store-requests-heading" className="card-title">
                    Store requests
                  </h2>
                  <p className="card-subtitle">
                    {storeRequests.length}{" "}
                    {storeRequests.length === 1 ? "request" : "requests"}{" "}
                    submitted
                  </p>
                </div>
                {canRequestStore ? null : (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate("/request-store")}
                  >
                    <Plus size={15} aria-hidden="true" /> New store request
                  </button>
                )}
              </div>
              <RequestList
                items={storeItems.slice(0, 8)}
                onOpen={navigate}
                emptyIcon={Store}
                emptyText="No requests yet. Submit one to get your store set up."
                emptyLabel="Request a store"
                onEmpty={() => navigate("/request-store")}
              />
            </section>
          </div>
        )}

        {activeTab === "website" && (
          <div className="dashboard-grid">
            {websiteRequestsError ? (
              <div className="grid-full">
                <InlineError
                  message={websiteRequestsError}
                  onRetry={fetchWebsiteRequests}
                />
              </div>
            ) : websiteRequestsLoading ? (
              <p role="status" className="grid-full card-subtitle">
                Loading your website requests...
              </p>
            ) : (
              <>
                <StatusCard
                  headingId="website-status-heading"
                  display={websiteDisplay}
                  status={websiteState}
                  trackerLabel="Website request stages"
                  onRun={runAction}
                  full={!latestWebsite}
                />
                {latestWebsite && (
                  <DetailsCard
                    headingId="website-details-heading"
                    title="Website details"
                    rows={websiteDetails}
                  />
                )}

                <section
                  className="dashboard-card list-card"
                  aria-labelledby="website-requests-heading"
                >
                  <div className="card-header dashboard-card-header-row">
                    <div>
                      <h2 id="website-requests-heading" className="card-title">
                        Website requests
                      </h2>
                      <p className="card-subtitle">
                        {websiteRequests.length}{" "}
                        {websiteRequests.length === 1 ? "request" : "requests"}{" "}
                        submitted
                      </p>
                    </div>
                    <Link
                      className="dashboard-card-link"
                      to="/website-requests"
                    >
                      View all
                      <ArrowRight size={15} aria-hidden="true" />
                    </Link>
                  </div>
                  <RequestList
                    items={websiteItems.slice(0, 8)}
                    onOpen={navigate}
                    emptyIcon={Globe}
                    emptyText="No website requests yet. Start a website project to see updates here."
                    emptyLabel="Start a website project"
                    onEmpty={() => navigate("/website-onboarding")}
                  />
                </section>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
